// Copyright ©️ 2026 eVolve MEP, LLC

import { type MachineElement, useEvolvePatch, type WithRequiredNotNull } from '@evolvemep/api-ts';
import { notifications } from '@mantine/notifications';

import { FormModal } from 'components/Mantine/Modals/FormModal';
import { isNotNil } from 'helpers/isNotNil';
import { BendElementDetailsForm } from 'modules/Shop/EvolveFabrication/BendElementDetails/BendElementDetailsForm';

type Props = {
  onClose: () => void;
  onBendUpdated: () => void;
  element: MachineElement | null;
};

const EditBendElementDetailsForm = ({ onClose, onBendUpdated, element }: WithRequiredNotNull<Props, 'element'>) => {
  const { apiCall: editBend } = useEvolvePatch(`shop/fabrication/element/${element.elementId}`);
  return (
    <BendElementDetailsForm
      isEditing
      elementValues={{
        location: element.location ?? '',
        level: element.level ?? '',
        runId: element.runId ?? '',
        description: element.comments ?? '',
      }}
      onSubmit={async ({ workOrder: _, ...elementData }) => {
        await editBend({ elementData });
        notifications.show({
          title: 'Successfully updated bend properties',
          message: `${element.name} has been updated.`,
          color: 'green',
        });
        onClose();
        onBendUpdated();
      }}
    />
  );
};

export const EditBendElementDetailsModal = ({ element, ...props }: Props) => (
  <FormModal
    title="Update other bend details"
    type="submit"
    opened={isNotNil(element)}
    onClose={props.onClose}
    startDirty
  >
    {isNotNil(element) && <EditBendElementDetailsForm element={element} {...props} />}
  </FormModal>
);
